import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Typography,
  Space,
  message,
  DatePicker,
  TimePicker,
  Modal,
  Spin,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../../configs"; // sesuaikan path
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";
import {
  autoCorrectLotNumber,
  LOT_NUMBER_FORMAT,
} from "../../../../utils/lotNumberValidator";

const { Text } = Typography;
const { Option } = Select;

const DOC_OPTIONS = [
  { label: "BC 1.6", value: 16 },
  { label: "BC 2.7", value: 27 },
  { label: "BC 4.0", value: 40 },
];

// generate SJ-YYMMDD001
const generateStrippingNumber = (seq = 1) => {
  const today = dayjs().format("YYMMDD");
  return `TLS-${today}${String(seq).padStart(3, "0")}`;
};

export default function FormPembongkaran() {
  const [form] = Form.useForm();
  const [nopenOptions, setNopenOptions] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [strippingNumber, setStrippingNumber] = useState(
    generateStrippingNumber(),
  );

  // simpan header incoming dari getDetilNopen
  const [incomingHeader, setIncomingHeader] = useState(null);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // Style untuk disabled input agar lebih kontras
  const disabledStyle = {
    backgroundColor: "#F3F6F9",
    color: "#3F4254",
    cursor: "not-allowed",
    border: "1px solid #E4E6EF",
  };

  // State untuk menyimpan warning Nomor Lot per blok
  // Key: "barangIndex-blockIndex", Value: warning message
  const [lotWarnings, setLotWarnings] = useState({});

  /**
   * Handler untuk auto-correct dan validasi Nomor Lot saat onBlur
   * @param {number} barangIndex - Index barang
   * @param {number} blockIndex - Index blok
   * @param {string} value - Nilai input
   */
  const handleLotNumberBlur = (barangIndex, blockIndex, value) => {
    if (!value) {
      // Clear warning jika kosong
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[`${barangIndex}-${blockIndex}`];
        return newWarnings;
      });
      return;
    }

    const result = autoCorrectLotNumber(value);
    const warningKey = `${barangIndex}-${blockIndex}`;

    if (result.isValid) {
      // Auto-correct dan clear warning
      const barang = form.getFieldValue("barang") || [];
      if (
        barang[barangIndex] &&
        barang[barangIndex].blocks &&
        barang[barangIndex].blocks[blockIndex]
      ) {
        barang[barangIndex].blocks[blockIndex].lotNumber = result.corrected;
        form.setFieldsValue({ barang });
        message.success(`Format Nomor Lot diperbaiki: ${result.corrected}`);
      }
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[warningKey];
        return newWarnings;
      });
    } else {
      // Set warning
      setLotWarnings((prev) => ({
        ...prev,
        [warningKey]: result.warning,
      }));
      message.warning(`Format Nomor Lot tidak valid: ${result.warning}`);
    }
  };

  // Ambil list Nopen berdasarkan jenis dokumen
  const handleJenisDokumenChange = async (idDocument) => {
    form.setFieldsValue({ document_number: undefined });
    setIncomingHeader(null);

    if (!idDocument) {
      setNopenOptions([]);
      return;
    }

    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/incoming/getListNopen", {
        params: { id_document: idDocument },
      });

      const list = res.data?.data || [];
      setNopenOptions(
        list.map((item) => ({
          label: `${item.document_number} - ${item.importir_name} (${dayjs(item.document_date).format("DD MMM YYYY")})`,
          // Use a unique value for the Select to distinguish between collisions
          value: `${item.document_number}|${dayjs(item.document_date).format("YYYY-MM-DD")}|${item.id_document}`,
        })),
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar No Dokumen Pabean");
      setNopenOptions([]);
    } finally {
      setLoadingNopen(false);
    }
  };

  // Ambil detail Nopen dan isi tabel barang
  const handleDocumentNumberChange = async (compositeValue) => {
    if (!compositeValue) return;

    const [noDocument, docDate, idDoc] = compositeValue.split("|");
    form.setFieldsValue({ document_number: compositeValue });

    // Generate TLS number based on Nopen (the real number part)
    const generatedTLS = `TLS-${dayjs().format("YYYYMMDD")}-${noDocument}`;
    setStrippingNumber(generatedTLS);
    form.setFieldsValue({ strippingNumber: generatedTLS });

    setLoadingDetail(true);
    try {
      const res = await axiosInstance.get("/incoming/getDetilNopen", {
        params: {
          no_document: noDocument,
          id_document: idDoc,
          document_date: docDate,
        },
      });

      const incoming = res.data?.header || null;
      const details = res.data?.details || [];
      setIncomingHeader(incoming);
      console.log("Detail Nopen:", incoming);

      if (!details.length) {
        message.warning("Tidak ada detail barang untuk dokumen ini");
        form.setFieldsValue({ barang: [] });
        return;
      }

      const barangFromDetails = details.map((d) => ({
        id_incoming_detail: d.id_incoming_detail,
        seri: d.seri,
        itemCode: d.item_code,
        itemName: d.item_name ? `${d.item_name} (${d.item_code})` : d.item_code, // fallback ke item_code jika nama kosong
        kemasan: d.total_kemasan, // total_kemasan → Jumlah Kemasan
        tallyKemasan: d.total_kemasan, // Default sama dengan kemasan
        catatan: "SESUAI", // Default SESUAI
        revisi: "N",
        blocks: [
          {
            gc: undefined,
            lotNumber: undefined,
            qty: d.total_kemasan, // Default sama dengan kemasan
            notes: undefined,
          },
        ],
      }));

      form.setFieldsValue({ barang: barangFromDetails });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil detail Nopen");
    } finally {
      setLoadingDetail(false);
    }
  };

  // Hitung catatan berdasarkan tally vs jumlah kemasan
  const handleTallyChange = (value, rowIndex) => {
    const barang = form.getFieldValue("barang") || [];
    const row = barang[rowIndex] || {};

    const jumlahKemasan = Number(row.kemasan || 0);
    const tally = Number(value || 0);

    let catatan = "";
    let revisi = "";
    if (!Number.isNaN(jumlahKemasan) && !Number.isNaN(tally)) {
      if (tally === jumlahKemasan) {
        catatan = "SESUAI";
        revisi = "N";
      } else if (tally > jumlahKemasan) {
        catatan = `LEBIH ${jumlahKemasan - tally}`;
        revisi = "Y";
      } else {
        catatan = `KURANG ${tally - jumlahKemasan}`;
        revisi = "Y";
      }
    }

    barang[rowIndex] = {
      ...row,
      tallyKemasan: value,
      catatan,
      revisi,
    };

    form.setFieldsValue({ barang });
  };

  useEffect(() => {
    form.setFieldsValue({
      strippingNumber: strippingNumber,
    });
  }, [strippingNumber, form]);

  // Triggered by "Simpan" button -> Open Confirmation Modal
  const onFinish = async (values) => {
    if (!incomingHeader || !incomingHeader.id_incoming) {
      message.error(
        "Data incoming belum terisi. Pilih No Dokumen Pabean terlebih dahulu.",
      );
      return;
    }
    setPendingValues(values);
    setConfirmVisible(true);
  };

  // Actual Submit after Confirmation
  const handleConfirmSubmit = async () => {
    try {
      setSaving(true);
      const values = pendingValues;

      // ambil array barang
      const barang = values.barang || [];

      // total_kemasan header: jumlah dari semua kemasan
      const totalKemasanHeader = barang.reduce((sum, row) => {
        const v = Number(row.kemasan || 0);
        return sum + (Number.isNaN(v) ? 0 : v);
      }, 0);

      // flatten barang + blocks → details
      const detailsPayload = [];
      barang.forEach((row) => {
        const blocks =
          row.blocks && row.blocks.length
            ? row.blocks
            : [{ gc: null, qty: null, notes: null }];

        blocks.forEach((block) => {
          detailsPayload.push({
            id_incoming_detail: row.id_incoming_detail,
            seri: row.seri,
            nopen: incomingHeader ? incomingHeader.document_number : null,
            tanggal_nopen: incomingHeader
              ? dayjs(incomingHeader.document_date).format("YYYY-MM-DD")
              : null,
            item_code: row.itemCode || null,
            id_warehouse: block.gc, // belum ada field gudang, set null dulu
            gc: block.lotNumber || null,
            qty_kemasan: Number(row.kemasan || 0), // jumlah kemasan referensi
            qty_stripping: Number(block.qty || 0), // jumlah di blok tsb
            catatan: block.notes || row.catatan || null,
            revisi: row.revisi, // revisi
          });
        });
      });

      const headerPayload = {
        id_incoming: incomingHeader.id_incoming,
        stripping_number: values.strippingNumber, // kalau ada API generate bisa diisi di sini
        stripping_date: dayjs(values.strippingDate).format("YYYY-MM-DD"),
        jam_mulai: values.jamMulai
          ? dayjs(values.jamMulai).format("HH:mm")
          : null,
        jam_selesai: values.jamSelesai
          ? dayjs(values.jamSelesai).format("HH:mm")
          : null,
        no_container: values.sealNumber || null, // sesuai permintaan: pakai nomor segel
        vendor_officer: "BUDIMAN", // hardcode dulu
        total_kemasan: incomingHeader.total_packaging || totalKemasanHeader,
        notes: null,
        document_number: incomingHeader ? incomingHeader.document_number : null,
        document_date: incomingHeader
          ? dayjs(incomingHeader.document_date).format("YYYY-MM-DD")
          : null,
        lampiran_1: null,
        lampiran_2: null,
        no_polisi: Array.isArray(values.platNumber)
          ? values.platNumber.join(",")
          : values.platNumber || null,
        created_by: 0,
        updated_by: 0,
      };

      const payload = {
        header: headerPayload,
        details: detailsPayload,
      };

      await axiosInstance.post("/stripping", payload);

      message.success("Data stripping berhasil disimpan");
      setConfirmVisible(false);
      form.resetFields();
      setIncomingHeader(null);
      setStrippingNumber(generateStrippingNumber()); // regenerate number
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data stripping");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Form Pembongkaran Barang
          </Title>
          <Text type="secondary">
            Kelola Pembongkaran Barang untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          barang: [
            {
              blocks: [
                {
                  gc: undefined,
                  lotNumber: undefined,
                  qty: undefined,
                  notes: undefined,
                },
              ],
            },
          ],
        }}
      >
        {/* ---------- BARIS ATAS: DOKUMEN PABEAN ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nomor Tallysheet</Text>}
              name="strippingNumber"
            >
              <Input placeholder="No Tallysheet" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Tallysheet</Text>}
              name="strippingDate"
              rules={[
                { required: true, message: "Tanggal Surat Jalan wajib diisi" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis Dokumen Pabean</Text>}
              name="id_document"
              rules={[
                { required: true, message: "Jenis dokumen wajib dipilih" },
              ]}
            >
              <Select
                placeholder="Jenis Dokumen Pabean"
                options={DOC_OPTIONS}
                onChange={handleJenisDokumenChange}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Dokumen Pabean</Text>}
              name="document_number"
              rules={[{ required: true, message: "No dokumen wajib dipilih" }]}
            >
              <Select
                placeholder="Pilih Nomor Dokumen Pabean"
                loading={loadingNopen || loadingDetail}
                disabled={DOC_OPTIONS.length === 0}
                showSearch
                optionFilterProp="label"
                onChange={handleDocumentNumberChange}
              >
                {nopenOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- BARIS KEDUA: PLAT & SEGEL ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Plat Kendaraan</Text>}
              name="platNumber"
              rules={[{ required: true, message: "No plat wajib diisi" }]}
              extra="Pisahkan dengan Enter"
            >
              <Select
                mode="tags"
                placeholder="No Plat Kendaraan"
                open={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nomor Segel</Text>}
              name="sealNumber"
            >
              <Input placeholder="No Segel Kendaraan" />
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- BARIS KETIGA: JAM MULAI & SELESAI ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jam Mulai Pembongkaran</Text>}
              name="jamMulai"
              rules={[{ required: true, message: "Jam mulai wajib diisi" }]}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Pilih Jam Mulai"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jam Selesai Pembongkaran</Text>}
              name="jamSelesai"
              rules={[{ required: true, message: "Jam selesai wajib diisi" }]}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Pilih Jam Selesai"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- LAMPIRAN / UPLOAD ---------- */}
        <Row style={{ marginTop: 8, marginBottom: 8 }}>
          <Col span={24}>
            <Text strong>Lampiran</Text>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
                onClick={() => {
                  document.getElementById("upload-lampiran").click();
                }}
              >
                Upload File
              </Button>
              <input
                id="upload-lampiran"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    message.success(
                      `File "${file.name}" siap diupload (handle upload terpisah).`,
                    );
                  }
                }}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF | DOC | DOCX | XLS |
                XLSX
                <br />
                Ukuran Maksimal File: 5 MB.
              </Text>
            </Space>
          </Col>
        </Row>

        <Spin spinning={loadingDetail} tip="Memuat data barang..." size="large">
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 36,
              borderRadius: 2,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            {/* Header tabel */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "0.8fr 3fr 1.2fr 1.2fr 1.8fr 80px",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <div>SERI</div>
              <div>NAMA BARANG *</div>
              <div>JUMLAH KEMASAN</div>
              <div>TALLY KEMASAN</div>
              <div>CATATAN</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="barang">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key}>
                      {/* Row utama barang */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "0.8fr 3fr 1.2fr 1.2fr 1.8fr 80px",
                          padding: "8px 16px",
                          borderTop: "1px solid #E4E6EF",
                          fontSize: 13,
                          alignItems: "center",
                          background: "#fff",
                        }}
                      >
                        {/* Seri */}
                        <Form.Item
                          {...field}
                          name={[field.name, "seri"]}
                          fieldKey={[field.fieldKey, "seri"]}
                          style={{
                            marginBottom: 0,
                            paddingRight: 8,
                          }}
                        >
                          <Input
                            disabled
                            placeholder="Seri"
                            style={disabledStyle}
                          />
                        </Form.Item>

                        {/* Nama Barang (item_code dari API) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "itemName"]}
                          fieldKey={[field.fieldKey, "itemName"]}
                          rules={[
                            {
                              required: true,
                              message: "Nama barang wajib diisi",
                            },
                          ]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            disabled
                            placeholder="Nama Barang"
                            style={disabledStyle}
                          />
                        </Form.Item>

                        {/* Jumlah Kemasan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "kemasan"]}
                          fieldKey={[field.fieldKey, "kemasan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            disabled
                            placeholder="Jumlah Kemasan"
                            style={disabledStyle}
                          />
                        </Form.Item>

                        {/* Tallying Kemasan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "tallyKemasan"]}
                          fieldKey={[field.fieldKey, "tallyKemasan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            placeholder="Tally Sheet"
                            onChange={(e) =>
                              handleTallyChange(e.target.value, field.name)
                            }
                          />
                        </Form.Item>

                        {/* Catatan: auto dari tally */}
                        <Form.Item
                          {...field}
                          name={[field.name, "catatan"]}
                          fieldKey={[field.fieldKey, "catatan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            disabled
                            placeholder="Catatan"
                            style={disabledStyle}
                          />
                        </Form.Item>

                        <div style={{ textAlign: "center" }}>
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                          />
                        </div>
                      </div>

                      {/* Detail Blok / Lot */}
                      <div
                        style={{
                          borderTop: "1px solid #F3F6F9",
                          padding: "8px 16px 16px",
                        }}
                      >
                        <Text strong>
                          <i>Detail Blok / Lot</i>
                        </Text>

                        <Form.List name={[field.name, "blocks"]}>
                          {(
                            blockFields,
                            { add: addBlock, remove: removeBlock },
                          ) => (
                            <>
                              {blockFields.map((blockField) => (
                                <Row
                                  key={blockField.key}
                                  gutter={8}
                                  style={{ marginTop: 8, alignItems: "center" }}
                                >
                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "gc"]}
                                      fieldKey={[blockField.fieldKey, "gc"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select placeholder="Rak Tujuan">
                                        {/* isi dari master warehouse/gc nanti */}
                                        <Option value="A">A</Option>
                                        <Option value="B">B</Option>
                                        <Option value="C">C</Option>
                                        <Option value="D">D</Option>
                                        <Option value="E">E</Option>
                                        <Option value="F">F</Option>
                                        <Option value="G">G</Option>
                                        <Option value="H">H</Option>
                                        <Option value="I">I</Option>
                                        <Option value="J">J</Option>
                                        <Option value="K">K</Option>
                                        <Option value="L">L</Option>
                                        <Option value="M">M</Option>
                                        <Option value="N">N</Option>
                                        <Option value="O">O</Option>
                                        <Option value="CL">CL (Chiller)</Option>
                                      </Select>
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "lotNumber"]}
                                      fieldKey={[
                                        blockField.fieldKey,
                                        "lotNumber",
                                      ]}
                                      style={{ marginBottom: 0 }}
                                      help={
                                        lotWarnings[
                                          `${field.name}-${blockField.name}`
                                        ] ? (
                                          <span
                                            style={{
                                              color: "#faad14",
                                              fontSize: 11,
                                            }}
                                          >
                                            ⚠️{" "}
                                            {
                                              lotWarnings[
                                                `${field.name}-${blockField.name}`
                                              ]
                                            }
                                          </span>
                                        ) : null
                                      }
                                      validateStatus={
                                        lotWarnings[
                                          `${field.name}-${blockField.name}`
                                        ]
                                          ? "warning"
                                          : undefined
                                      }
                                      tooltip="Format: {Blok}{Posisi}.{Nomor} - Contoh: AG.1, BX.20"
                                    >
                                      <Input
                                        placeholder="Nomor Lot (cth: AG.1)"
                                        onBlur={(e) =>
                                          handleLotNumberBlur(
                                            field.name,
                                            blockField.name,
                                            e.target.value,
                                          )
                                        }
                                        style={
                                          lotWarnings[
                                            `${field.name}-${blockField.name}`
                                          ]
                                            ? {
                                                borderColor: "#faad14",
                                                backgroundColor: "#fffbe6",
                                              }
                                            : undefined
                                        }
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "qty"]}
                                      fieldKey={[blockField.fieldKey, "qty"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input placeholder="Jumlah Barang" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={7}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "notes"]}
                                      fieldKey={[blockField.fieldKey, "notes"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input placeholder="Catatan" />
                                    </Form.Item>
                                  </Col>
                                  <Col
                                    xs={24}
                                    md={2}
                                    style={{ textAlign: "center" }}
                                  >
                                    <Button
                                      type="text"
                                      icon={
                                        <DeleteOutlined
                                          style={{ color: "#F64E60" }}
                                        />
                                      }
                                      onClick={() =>
                                        removeBlock(blockField.name)
                                      }
                                    />
                                  </Col>
                                </Row>
                              ))}

                              <Button
                                type="dashed"
                                size="small"
                                onClick={() =>
                                  addBlock({
                                    gc: undefined,
                                    lotNumber: undefined,
                                    qty: undefined,
                                    notes: undefined,
                                  })
                                }
                                style={{ marginTop: 8 }}
                                icon={<PlusOutlined />}
                              >
                                Tambah Blok
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Card>
        </Spin>

        {/* tombol simpan / batal */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Space>
            <Button danger>Batal</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Simpan
            </Button>
          </Space>
        </Row>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Penyimpanan"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data pembongkaran berikut?</p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>
                    No Tallysheet
                  </td>
                  <td style={{ padding: 4 }}>
                    : {pendingValues.strippingNumber}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>Tanggal</td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {dayjs(pendingValues.strippingDate).format("DD MMMM YYYY")}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>No Dokumen</td>
                  <td style={{ padding: 4 }}>
                    : {pendingValues.document_number}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>No Plat</td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {Array.isArray(pendingValues.platNumber)
                      ? pendingValues.platNumber.join(", ")
                      : pendingValues.platNumber}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>Jam Mulai</td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {pendingValues.jamMulai
                      ? dayjs(pendingValues.jamMulai).format("HH:mm")
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>
                    Jam Selesai
                  </td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {pendingValues.jamSelesai
                      ? dayjs(pendingValues.jamSelesai).format("HH:mm")
                      : "-"}
                  </td>
                </tr>
              </tbody>
            </table>

            <Text strong>Ringkasan Barang:</Text>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {(pendingValues.barang || []).map((item, idx) => (
                <li key={idx} style={{ marginBottom: 8 }}>
                  <div>
                    {item.itemName} - <b>{item.tallyKemasan}</b> /{" "}
                    {item.kemasan} Kemasan
                    {item.catatan && item.catatan !== "SESUAI" && (
                      <span style={{ color: "red", marginLeft: 8 }}>
                        ({item.catatan})
                      </span>
                    )}
                  </div>
                  {item.blocks && item.blocks.length > 0 && (
                    <div style={{ fontSize: 12, color: "#888", marginLeft: 0 }}>
                      Lokasi:{" "}
                      {item.blocks
                        .map(
                          (b) =>
                            `${b.gc || "?"} (Lot: ${b.lotNumber || "-"}, Qty: ${
                              b.qty || 0
                            })`,
                        )
                        .join(", ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </Card>
  );
}
